import { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { BookMarked, ChevronDown, Plus, Settings, UserCircle, UserCog } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { cn } from '@/lib/cn';
import { useReadersStore } from '@/store/readers.store';
import { useAccountStore } from '@/store/account.store';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { CoinBadge } from '@/components/coins';
import { coinsService } from '@/services/coins.service';
import { trialDaysRemaining } from '@/lib/trial';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useGuestAuthActions } from '@/hooks/useGuestAuthActions';
import { Button } from '@/components/ui/Button';

/**
 * BrandHeader — the sticky top nav that wraps every authenticated page.
 * Owl logo + wordmark on the left, nav links in the middle, reader
 * selector pill + user avatar on the right. The reader's personal
 * "Bookshelf" page lives inside the reader-selector dropdown rather than
 * the top nav — the destination is intrinsically tied to the active
 * reader, so it belongs alongside reader switching.
 */
export function BrandHeader() {
  const isGuest = useIsGuest();
  const { signIn, createAccount } = useGuestAuthActions();
  const selected = useReadersStore((s) => s.selectedReader());
  const readers = useReadersStore((s) => s.readers);
  const setSelectedReaderId = useReadersStore((s) => s.setSelectedReaderId);
  const setCoinBalance = useReadersStore((s) => s.setCoinBalance);
  const selectedCoinBalance = useReadersStore((s) => s.selectedCoinBalance());
  const isAdmin = useAccountStore((s) => s.isAdmin());
  const account = useAccountStore((s) => s.account);
  const isPaidSubscriber = useAccountStore((s) => s.isPaidSubscriber());
  const isInTrial = useAccountStore((s) => s.isInTrial());
  const navigate = useNavigate();

  // Coin balance hydration. Fires whenever the selected reader changes,
  // refreshing the cached number in the readers store. Soft-fails —
  // the chip renders a "—" placeholder while the request is in flight
  // and simply stays that way if the request errors (no toast; this is
  // ambient data, not a core flow).
  const selectedReaderId = selected?.id ?? null;
  useEffect(() => {
    if (!selectedReaderId) return;
    let cancelled = false;
    (async () => {
      try {
        const summary = await coinsService.getSummary(selectedReaderId);
        if (!cancelled) setCoinBalance(selectedReaderId, summary.balance);
      } catch {
        // Quietly ignore — the header chip can live with "—".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedReaderId, setCoinBalance]);
  // Upgrade pill is shown to free-tier users only. We hide it from
  // admins because the admin view is the operator's perspective on the
  // product, and a "Buy a subscription" CTA there is just visual noise.
  // Trial users pass `isPaidSubscriber` (access is granted), so this is
  // false for them — they get the distinct trial-countdown pill instead.
  const showUpgradePill = !isPaidSubscriber && !isAdmin && account !== null;
  // Trial countdown pill: a global nudge to convert before the free trial
  // ends. Hidden from admins (operator view). Replaces the upgrade pill.
  const showTrialPill = isInTrial && !isAdmin && account !== null;
  // Whole days left in the trial — recomputed against the clock (never the
  // persisted boolean) so the count is accurate on a returning visit. Only
  // read when `showTrialPill` is true, where it's guaranteed >= 1.
  const trialDaysLeft = trialDaysRemaining(account?.subscription?.trialEndsAt ?? null);
  const trialDaysLabel = trialDaysLeft === 1 ? '1 day left' : `${trialDaysLeft} days left`;

  // Guest header: logo + a single Library link + sign-in / create-account.
  // No reader switcher, coins, or upgrade pill — those need an account.
  if (isGuest) {
    return (
      <header className="sticky top-0 z-30 border-b border-or-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] w-full max-w-[1200px] items-center gap-3 px-4 sm:gap-6 sm:px-6">
          <NavLink
            to="/library"
            className="flex shrink-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
            aria-label="OllieReads home"
          >
            <img
              src="/brand/ollie_logo_square.png"
              alt=""
              width={40}
              height={40}
              className="shrink-0"
            />
            <span className="hidden font-display text-[22px] font-bold text-or-orange-800 sm:inline">
              Ollie<span className="text-or-orange-500">Reads</span>
            </span>
          </NavLink>

          <nav
            aria-label="Primary"
            className="mt-2 flex items-center gap-4 text-[13px] font-semibold text-or-slate-600 sm:gap-6 sm:text-[14px]"
          >
            <HeaderLink to="/library">Library</HeaderLink>
            <HeaderLink to="/catalog" className="hidden sm:inline">
              Browse
            </HeaderLink>
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => void signIn()}
              className="text-[13px] font-semibold text-or-slate-600 transition hover:text-or-orange-700"
            >
              Sign in
            </button>
            <Button size="md" onClick={() => void createAccount()}>
              Create account
            </Button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-30 border-b border-or-slate-200 bg-white">
      <div className="mx-auto flex h-[68px] w-full max-w-[1200px] items-center gap-3 px-4 sm:gap-6 sm:px-6">
        <NavLink
          to="/library"
          className="flex shrink-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
          aria-label="OllieReads home"
        >
          <img
            src="/brand/ollie_logo_square.png"
            alt=""
            width={40}
            height={40}
            className="shrink-0"
          />
          <span className="hidden font-display text-[22px] font-bold text-or-orange-800 sm:inline">
            Ollie<span className="text-or-orange-500">Reads</span>
          </span>
        </NavLink>

        {selected ? (
          <CoinBadge
            balance={selectedCoinBalance}
            onClick={() => navigate('/rewards')}
            ariaLabel={
              selectedCoinBalance === null
                ? `${selected.name}'s coin balance loading`
                : `${selected.name} has ${Math.max(selectedCoinBalance, 0)} coin${
                    Math.max(selectedCoinBalance, 0) === 1 ? '' : 's'
                  }`
            }
          />
        ) : null}

        {/* Primary nav. No `overflow-x-auto` / `min-w-0` here on purpose:
            setting overflow on one axis implicitly enables it on the other,
            and the active underline (absolutely positioned at -bottom-[22px]
            on each HeaderLink) sits outside the nav's content box —
            triggering a vertical scrollbar. The three short labels also
            always fit at every supported breakpoint, so scroll fallback is
            unnecessary. */}
        <nav
          aria-label="Primary"
          className="mt-2 flex items-center gap-4 text-[13px] font-semibold text-or-slate-600 sm:gap-6 sm:text-[14px]"
        >
          <HeaderLink to="/library">Library</HeaderLink>
          <LearnsNavLink />
          <HeaderLink to="/rewards" className="hidden sm:inline">
            Rewards
          </HeaderLink>
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-full border border-or-orange-200 bg-or-orange-50 px-3 py-1.5 transition-colors hover:bg-or-orange-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
                aria-label={
                  selected ? `Reading as ${selected.name}. Open menu.` : 'Open account menu'
                }
              >
                {selected ? (
                  <>
                    <ReaderAvatar name={selected.name} avatarSlug={selected.avatarSlug} size="xs" />
                    <span className="hidden text-[12px] font-semibold text-or-orange-800 sm:inline">
                      Reading as {selected.name}
                    </span>
                  </>
                ) : (
                  <UserCircle className="h-5 w-5 text-or-orange-700" aria-hidden />
                )}
                <ChevronDown className="h-4 w-4 text-or-orange-700" aria-hidden />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={8}
                className="z-50 min-w-[240px] overflow-hidden rounded-2xl border border-or-slate-200 bg-white p-1 shadow-lg"
              >
                {selected ? (
                  <>
                    <DropdownMenu.Item
                      onSelect={() => navigate('/list')}
                      className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-[13px] font-semibold text-or-orange-800 outline-none data-[highlighted]:bg-or-orange-50"
                    >
                      <BookMarked className="h-4 w-4" aria-hidden />
                      Visit {selected.name}'s Bookshelf
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="my-1 h-px bg-or-slate-100" />
                    <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-or-slate-500">
                      Readers on this account
                    </div>
                    {readers.map((reader) => (
                      <DropdownMenu.Item
                        key={reader.id}
                        onSelect={() => setSelectedReaderId(reader.id)}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-[13px] text-or-slate-700 outline-none',
                          'data-[highlighted]:bg-or-orange-50 data-[highlighted]:text-or-orange-800',
                          reader.id === selected.id && 'bg-or-cream-50 font-semibold',
                        )}
                      >
                        <ReaderAvatar name={reader.name} avatarSlug={reader.avatarSlug} size="sm" />
                        <span className="truncate">{reader.name}</span>
                      </DropdownMenu.Item>
                    ))}
                    <DropdownMenu.Separator className="my-1 h-px bg-or-slate-100" />
                    <DropdownMenu.Item
                      onSelect={() => navigate('/readers')}
                      className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-[13px] text-or-slate-700 outline-none data-[highlighted]:bg-or-slate-100"
                    >
                      <UserCog className="h-4 w-4" aria-hidden />
                      Manage readers
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onSelect={() => navigate('/readers?new=1')}
                      className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-[13px] text-or-slate-700 outline-none data-[highlighted]:bg-or-slate-100"
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                      Add a reader
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="my-1 h-px bg-or-slate-100" />
                  </>
                ) : null}
                <DropdownMenu.Item
                  onSelect={() => navigate('/account')}
                  className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-[13px] text-or-slate-700 outline-none data-[highlighted]:bg-or-slate-100"
                >
                  <Settings className="h-4 w-4" aria-hidden />
                  Account
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>

      {/* Subscription CTA banner — a thin full-width strip directly below the
          nav row (still inside the sticky header so it stays visible). Replaces
          the former header pill. Shown to free and trial users; paid
          subscribers, admins, and guests see nothing. */}
      {showTrialPill || showUpgradePill ? (
        <button
          type="button"
          onClick={() => navigate('/pricing')}
          className="flex w-full items-center justify-center border-t border-or-orange-200 bg-or-orange-100 px-4 py-1.5 text-[12px] font-semibold text-or-orange-800 transition-colors hover:bg-or-orange-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-or-orange-500/40 sm:text-[13px]"
        >
          {showTrialPill
            ? `Free trial · ${trialDaysLabel} · Upgrade to Premium!`
            : 'Upgrade to Premium!'}
        </button>
      ) : null}
    </header>
  );
}

function HeaderLink({
  to,
  children,
  className,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'relative pb-1 transition-colors hover:text-or-orange-700',
          isActive && 'text-or-orange-700',
          className,
        )
      }
    >
      {({ isActive }) => (
        <>
          <span>{children}</span>
          <span
            className={cn(
              'absolute inset-x-0 -bottom-[22px] h-[2px] rounded-full bg-or-orange-500 transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
            aria-hidden
          />
        </>
      )}
    </NavLink>
  );
}

/**
 * LearnsNavLink — the "Learn" link in the top nav. Intentionally
 * styled in the Learns teal accent (not the orange used by the rest of
 * the nav) so the new product reads as a sibling destination. Carries a
 * small new-feature dot until the product exits beta.
 */
function LearnsNavLink() {
  return (
    <NavLink
      to="/learns"
      className={({ isActive }) => cn('relative pb-1 transition-colors', isActive && 'font-bold')}
      style={{ color: '#1F6A6A', fontWeight: 600 }}
    >
      {({ isActive }) => (
        <>
          <span className="inline-flex items-center" style={{ gap: '6px' }}>
            <span>Learn</span>
          </span>
          <span
            className={cn(
              'absolute inset-x-0 -bottom-[22px] h-[2px] rounded-full transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
            style={{ backgroundColor: '#2E8A8A' }}
            aria-hidden
          />
        </>
      )}
    </NavLink>
  );
}
